import {
  IOrganizationApi,
  IOrganization,
  IImomoResponse,
  IForecast,
  IForecastModel,
  IForecastType,
  IDecadalBulletinSettings,
  ITemplateTagsResponse,
  ITemplate,
  ITemplateResponse,
  IReport
} from './api.types';
import * as moment from 'moment';

export default class OrganizationApi implements IOrganizationApi {
  static $inject = ['$http', 'backendUrl'];

  private endpoint:string;
  private organizationReportsEndpoint: string;

  constructor(private $http:ng.IHttpService, private backendUrl: string) {
    this.endpoint = `${backendUrl}/organizations`;
    this.organizationReportsEndpoint = `${backendUrl}/reports`;
  }

  getOrganizations():ng.IPromise<IOrganization[]> {
    return this.$http<IImomoResponse<IOrganization>>({
      url: this.endpoint,
      method: 'GET'
    }).then((httpRes) => httpRes.data.resources);
  }

  getOrganization(id: number): ng.IPromise<IOrganization> {
      return this.$http<IImomoResponse<IOrganization>>({
          url: `${this.endpoint}/` + id,
          method: 'GET'
      }).then(httpData => httpData.data.resources[0]);
  }

  deleteOrganization(id: number): ng.IPromise<void> {
      return this.$http({
          url: `${this.endpoint}/` + id,
          method: 'DELETE'
      }).then(function(data) {
      });
  }

  saveOrganization(organization: IOrganization): ng.IPromise<IOrganization> {
      return this.$http<IImomoResponse<IOrganization>>({
          url: `${this.endpoint}` + (organization.id ? `/${organization.id}`: ''),
          method: organization.id ? 'PUT' : 'POST',
          data: organization
      }).then(httpData => httpData.data.resources[0]);
  }

  createForecastType(orgId: number, forecastType: IForecastType):ng.IPromise<void> {
    return this.$http({
      url: `${this.endpoint}/${orgId}/forecast_types`,
      method: 'POST',
      data: forecastType
    }).then((httpData) => {
    });
  }

  updateForecastType(orgId: number, forecastType: IForecastType):ng.IPromise<void> {
    return this.$http({
      url: `${this.endpoint}/${orgId}/forecast_types/${forecastType.id}`,
      method: 'PUT',
      data: forecastType
    }).then((httpData) => {
    });
  }

  deleteForecastType(orgId: number, forecastType: IForecastType):ng.IPromise<void> {
    return this.$http({
      url: `${this.endpoint}/${orgId}/forecast_types/${forecastType.id}`,
      method: 'DELETE',
      data: forecastType
    }).then((httpData) => {
    });
  }

  getForecastTypes(orgId: number): ng.IPromise<IForecastType[]> {
    return this.$http<IImomoResponse<IForecastType>>({
      url: `${this.endpoint}/${orgId}/forecast_types`,
      method: 'GET'
    }).then(httpData => {
      return httpData.data.resources;
    });
  }
  
  generateForecastBulletin(id: number, siteIds: number[]):ng.IPromise<string> {
    return this.$http<IImomoResponse<{url:string}>>({
          url: `${this.endpoint}/${id}/forecast_bulletin`,
          method: 'POST',
          data: {
            date: new Date(),
            siteIds: siteIds
          }
      }).then(httpData => httpData.data.resources[0].url);
  }

  getReportingTemplateTags(reportType: string):ng.IPromise<ITemplateTagsResponse> {
    return this.$http<IImomoResponse<ITemplateTagsResponse>>({
          url: `${this.organizationReportsEndpoint}/${reportType}/template_tags`,
          method: 'GET'
      }).then(httpData => {
        return httpData.data.resources[0];
      });
  }

  saveReportingTemplate(reportType: string, data: ITemplate):ng.IPromise<ITemplateTagsResponse> {
    return this.$http<IImomoResponse<ITemplateTagsResponse>>({
          url: `${this.organizationReportsEndpoint}/${reportType}/template`,
          method: 'POST',
          data: data
      }).then(httpData => {
        return httpData.data.resources[0];
      });
  }

  deleteReportingTemplate(reportType: string):ng.IPromise<void> {
    return this.$http({
      url: `${this.organizationReportsEndpoint}/${reportType}/template`,
      method: 'DELETE'
    }).then((httpData) => {
    });
  }

  getReportingTemplateDefault(reportType: string):ng.IPromise<string> {
    return this.$http<IImomoResponse<{url:string}>>({
          url: `${this.organizationReportsEndpoint}/${reportType}/template/default`,
          method: 'GET'
      }).then(httpData => {
        return httpData.data.resources[0].url;
      });
  }

  getReportingTemplate(reportType: string):ng.IPromise<ITemplateResponse> {
    return this.$http<IImomoResponse<ITemplateResponse>>({
          url: `${this.organizationReportsEndpoint}/${reportType}/template`,
          method: 'GET'
      }).then(httpData => {
        return httpData.data.resources[0];
      });
  }

  renewPayment(id: number): ng.IPromise<IOrganization> {
    return this.$http<IImomoResponse<IOrganization>>({
        url: `${this.endpoint}/` + id + '/renew',
        method: 'POST'
    }).then(function(httpData) {
      return httpData.data.resources[0];
    });
  }

  generateBulletin(reportType: string, stationIds:number[], date:moment.Moment): ng.IPromise<IReport> {
    return this.$http<IImomoResponse<IReport>>({
          url: `${this.organizationReportsEndpoint}/${reportType}/generate`,
          method: 'POST',
          data: {
            date: date,
            siteIds: stationIds
          }
      }).then(httpData => httpData.data.resources[0]);
  }

  saveDecadalBulletinSettings(settings: IDecadalBulletinSettings): ng.IPromise<IDecadalBulletinSettings> {
    return this.$http<IImomoResponse<IDecadalBulletinSettings>>({
        url: `${this.organizationReportsEndpoint}/decadal_bulletin/schedule`,
        method: 'POST',
        data: settings
    }).then(function(httpData) {
      return httpData.data.resources[0];
    });
  }

  deleteDecadalBulletinSettings(): ng.IPromise<void> {
    return this.$http({
        url: `${this.organizationReportsEndpoint}/decadal_bulletin/schedule`,
        method: 'DELETE'
    }).then(function(httpData) {
    });
  }

  getDecadalBulletinSettings(): ng.IPromise<IDecadalBulletinSettings> {
    return this.$http<IImomoResponse<IDecadalBulletinSettings>>({
        url: `${this.organizationReportsEndpoint}/decadal_bulletin/schedule`,
        method: 'GET'
    }).then(function(httpData) {
      return httpData.data.resources[0];
    });
  }
}



/** WEBPACK FOOTER **
 ** ./app/api/organization-api.service.ts
 **/